import { useEffect, useMemo, useState } from "react";
import type { Midi, MidiChannel } from "@midi-fantom-utils/midi-core";
import {
  PATTERN_SLOTS,
  buildPattern,
  countNotesInMeasureRange,
  type Pattern,
  type PatternId,
  type PatternSlot,
  type SliceRange,
  type TrackNumber,
  formatPatternId,
} from "@midi-fantom-utils/midi-util";

export type ChannelNoteCount = { channel: number; noteCount: number };

export type PatternDialogProps = {
  open: boolean;
  source: Midi;
  range: SliceRange;
  fileBaseName: string;
  existing: ReadonlyMap<PatternId, Pattern>;
  channelCounts: ReadonlyArray<ChannelNoteCount>;
  onClose: () => void;
  onCreate: (pattern: Pattern) => void;
  /** Префилл имени (напр. «Метка · ch N»). */
  defaultName?: string;
};

const TRACK_NUMBERS: TrackNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

export const PatternDialog = ({
  open,
  source,
  range,
  fileBaseName,
  existing,
  channelCounts,
  onClose,
  onCreate,
  defaultName,
}: PatternDialogProps) => {
  const [track, setTrack] = useState<TrackNumber>(1);
  const [slot, setSlot] = useState<PatternSlot>("A");
  const [channel, setChannel] = useState<MidiChannel>(range.channel as MidiChannel);
  const [name, setName] = useState<string>("");
  const [length, setLength] = useState<number>(range.endMeasure - range.startMeasure);
  const [confirmOverwrite, setConfirmOverwrite] = useState<Pattern | null>(null);

  useEffect(() => {
    if (!open) return;
    setChannel(range.channel as MidiChannel);
    setTrack(1);
    setSlot("A");
    setName(defaultName ?? "");
    setLength(Math.max(1, range.endMeasure - range.startMeasure));
    setConfirmOverwrite(null);
  }, [open, range, defaultName]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const id = useMemo(() => formatPatternId(track, slot), [track, slot]);
  const conflict = existing.get(id);
  // Считаем ноты внутри диапазона `[startMeasure, endMeasure)` для
  // выбранного канала. Раньше использовался `channelCounts` (по всему
  // файлу), что приводило к активной кнопке «Создать» для среза без
  // нот: `buildPattern` тихо возвращал `{ ok: false, reason: "empty" }`.
  const noteCountInSlice = useMemo(() => {
    if (!source) return 0;
    return countNotesInMeasureRange(
      source,
      channel,
      range.startMeasure,
      range.endMeasure
    );
  }, [source, channel, range.startMeasure, range.endMeasure]);

  if (!open) return null;

  const handleCreate = () => {
    const result = buildPattern(
      source,
      track,
      slot,
      { ...range, channel },
      {
        fileBaseName,
        existing,
        name: name || undefined,
        lengthMeasures: length,
        channel,
      }
    );
    if (!("ok" in result) || result.ok === false) {
      if ("conflict" in result) {
        setConfirmOverwrite(result.conflict.existing);
        return;
      }
      // empty/too_long — кнопка должна быть disabled
      return;
    }
    onCreate(result.pattern);
    onClose();
  };

  const handleOverwrite = () => {
    if (!confirmOverwrite) return;
    // Удаляем старый и создаём новый.
    const next = new Map(existing);
    next.delete(confirmOverwrite.id);
    const result = buildPattern(
      source,
      track,
      slot,
      { ...range, channel },
      {
        fileBaseName,
        existing: next,
        name: name || undefined,
        lengthMeasures: length,
        channel,
      }
    );
    if (!("ok" in result) || result.ok === false) return;
    onCreate(result.pattern);
    onClose();
  };

  const isOkDisabled = noteCountInSlice === 0;

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="settings-modal__backdrop"
      onMouseDown={onBackdropMouseDown}
      role="presentation"
    >
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pattern-dialog-title"
      >
        <header className="settings-modal__head">
          <h2 id="pattern-dialog-title" className="settings-modal__title">
            Создать паттерн
          </h2>
          <button
            type="button"
            className="btn btn--ghost settings-modal__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </header>
        <div className="settings-modal__body">
          {confirmOverwrite ? (
            <div>
              <p>
                Слот <strong>{confirmOverwrite.id}</strong> уже занят паттерном «{confirmOverwrite.name}». Перезаписать?
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleOverwrite}
                >
                  Перезаписать
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setConfirmOverwrite(null)}
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <div className="settings-section">
              <div className="settings-row">
                <span className="settings-row__label">Трек</span>
                <select
                  className="settings-row__input"
                  value={track}
                  onChange={(e) => setTrack(Number(e.target.value) as TrackNumber)}
                >
                  {TRACK_NUMBERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <span className="settings-row__label">Слот</span>
                <select
                  className="settings-row__input"
                  value={slot}
                  onChange={(e) => setSlot(e.target.value as PatternSlot)}
                >
                  {PATTERN_SLOTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                      {existing.has(formatPatternId(track, s)) ? " (занят)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="settings-row">
                <span className="settings-row__label">Канал</span>
                <select
                  className="settings-row__input"
                  value={channel}
                  onChange={(e) => setChannel(Number(e.target.value) as MidiChannel)}
                >
                  {channelCounts.map((c) => (
                    <option key={c.channel} value={c.channel}>
                      {c.channel === 9 ? `${c.channel} — Drums (GM kit)` : c.channel} · {c.noteCount} нот (в файле)
                    </option>
                  ))}
                </select>
                <span className="muted" style={{ fontSize: 11 }}>
                  в срезе: {noteCountInSlice}
                </span>
              </div>
              <div className="settings-row">
                <span className="settings-row__label">Имя</span>
                <input
                  className="settings-row__input"
                  type="text"
                  value={name}
                  placeholder={`${fileBaseName} T${String(track).padStart(2, "0")}-${slot}`}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="settings-row">
                <span className="settings-row__label">Длина (тактов)</span>
                <input
                  className="settings-row__input"
                  type="number"
                  min={1}
                  max={64}
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                />
              </div>
              {isOkDisabled ? (
                <p className="kv__diff">
                  В этом срезе нет нот для выбранного канала.
                </p>
              ) : null}
            </div>
          )}
        </div>
        {!confirmOverwrite ? (
          <footer className="settings-modal__foot">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Отмена
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleCreate}
              disabled={isOkDisabled}
            >
              Создать
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
};
